import { EnginePhase, Seat, STANDBY_HOLD_MS } from '../types/state';

/** Minimal engine surface the Standby hold timer needs. */
export interface StandbyHoldSource {
  getState(): { status: string; phase: EnginePhase; turnPlayer: Seat };
  hasPendingResponse(): boolean;
}

/**
 * Host-owned timer: after the duel sits idle on Standby, fire ADVANCE_PHASE
 * for the turn player. Cleared when anything else happens (player skip, trap
 * window, match end) so PvP clients never race two ADVANCE_PHASE sends.
 *
 * If a response window is open when the delay elapses, the timer reschedules
 * instead of going silent — otherwise Standby can stick after a trap prompt.
 */
export class StandbyHoldTimer {
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly source: StandbyHoldSource,
    private readonly onFire: (seat: Seat) => void,
    private readonly delayMs: number = STANDBY_HOLD_MS,
  ) {}

  /** Cancel a pending Standby → Main 1 advance. */
  clear(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  /**
   * Arm when the match is on Standby. Keeps polling through open response
   * windows; no-op once the phase has moved on or the match ended.
   */
  arm(): void {
    this.clear();
    this.schedule();
  }

  private schedule(): void {
    if (!this.onStandby()) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.onStandby()) return;
      if (this.source.hasPendingResponse()) {
        this.schedule();
        return;
      }
      this.onFire(this.source.getState().turnPlayer);
    }, this.delayMs);
  }

  private onStandby(): boolean {
    const state = this.source.getState();
    return state.status === 'active' && state.phase === EnginePhase.STANDBY;
  }
}
